import React from 'react';
import { Rect, Text, Group } from 'react-konva';
import { useCanvasStore } from '../../store/canvasStore';
import { useEditorStore } from '../../store/editorStore';
import { THEMES } from '../../floorplanTheme';
import { dimensionStrings, roomTextLayout } from '../../canvasConstants';
import type { CanvasShape } from '../../types';

interface RoomShapeProps {
  shape: CanvasShape;
  onCanvasChange?: () => void;
}

const RoomShape: React.FC<RoomShapeProps> = ({ shape, onCanvasChange }) => {
  const { updateShape, removeShape, pushHistory } = useCanvasStore();
  const { selectedIds, setSelectedIds, activeTool, canvasTheme } = useEditorStore();
  const theme = THEMES[canvasTheme];
  const isSelected = selectedIds.includes(shape.id);

  const handleSelect = () => {
    if (activeTool === 'select') setSelectedIds([shape.id]);
    if (activeTool === 'eraser') {
      removeShape(shape.id);
      pushHistory();
      onCanvasChange?.();
    }
  };

  const handleDragEnd = (e: any) => {
    updateShape(shape.id, { x: e.target.x(), y: e.target.y() });
    pushHistory();
    onCanvasChange?.();
  };

  const handleTransformEnd = (e: any) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    updateShape(shape.id, {
      x: node.x(),
      y: node.y(),
      width: Math.max(20, (shape.width ?? 100) * scaleX),
      height: Math.max(20, (shape.height ?? 80) * scaleY),
      rotation: node.rotation(),
    });
    pushHistory();
    onCanvasChange?.();
  };

  const w = Math.max(20, shape.width ?? 100);
  const h = Math.max(20, shape.height ?? 80);

  const { imperial, metric } = dimensionStrings(w, h);
  const text = roomTextLayout(w, h);
  const labelText = (shape.label ?? '').toUpperCase();
  const ink = canvasTheme === 'light' ? '#1a1a1a' : '#f4f4f8';

  return (
    <Group
      id={shape.id}
      name="transformable"
      x={shape.x}
      y={shape.y}
      rotation={shape.rotation ?? 0}
      draggable={activeTool === 'select'}
      onClick={handleSelect}
      onTap={handleSelect}
      onDragEnd={handleDragEnd}
      onTransformEnd={handleTransformEnd}
    >
      <Rect
        width={w}
        height={h}
        fill="rgba(0,0,0,0.001)"
        stroke={isSelected ? theme.wall.selectedStroke : undefined}
        strokeWidth={isSelected ? 1.5 : 0}
        dash={isSelected ? [8, 6] : undefined}
        opacity={shape.opacity ?? 1}
      />
      {labelText && (
        <Text
          text={labelText}
          x={8}
          y={text.labelY}
          width={Math.max(20, w - 16)}
          align="center"
          fontSize={text.labelSize}
          fontStyle="bold"
          fontFamily="system-ui, -apple-system, sans-serif"
          fill={ink}
          listening={false}
        />
      )}
      {text.showDims && (
        <Text
          text={`${imperial}\n${metric}`}
          x={8}
          y={text.dimY}
          width={Math.max(20, w - 16)}
          align="center"
          fontSize={text.dimSize}
          lineHeight={1.25}
          fontStyle="bold"
          fontFamily="system-ui, -apple-system, sans-serif"
          fill={ink}
          listening={false}
        />
      )}
    </Group>
  );
};

export default RoomShape;
